import { useCallback, useEffect, useMemo, useState } from "react";
import { AxiosError } from "axios";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { exportExpressData, getExpressData } from "../../services";
import { pushGlobalError } from "../../errors/globalError";
import {
  getDefaultExpressDate,
  getExpressDataCsvFilename,
  getExpressDataSummary,
  getExpressDataTransactionDateParam,
  getFilenameFromContentDisposition,
} from "./utils";
import {
  ExpressDataListResponse,
  ExpressDataRow,
  ExpressDataStatus,
  ExpressDataTransactionType,
} from "../../types/express-data.type";
import { getProfile } from "../../services/keycloak";
import { featureAccess } from "../../permissions/access";
import { useUserRole } from "../../utils/hooks/useUserRole";
import { useBranches } from "../SpecialOrder/useBranches";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import MenuItem from "@mui/material/MenuItem";
import Pagination from "@mui/material/Pagination";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import DownloadIcon from "@mui/icons-material/Download";
import ExpressDataFilter from "./ExpressDataFilter";
import ExpressDataSummaryCards from "./ExpressDataSummaryCards";
import ExpressDataTable from "./ExpressDataTable";
import { COMPACT_QUERY } from "../../utils/hooks/useCompact";

dayjs.locale("th");

const compact = "@media " + COMPACT_QUERY;

const LIST_LIMIT = [10, 20, 50, 100];

const EMPTY_EXPRESS_DATA_RESPONSE: ExpressDataListResponse = {
  rows: [],
  count: 0,
  page: 1,
  limit: 10,
  totalPages: 0,
  total_net_value: 0,
};

const downloadCsv = (filename: string, blob: Blob) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const getExportErrorMessage = async (error: unknown) => {
  const responseData = (error as AxiosError<Blob>).response?.data;
  if (!(responseData instanceof Blob)) return "";

  const text = await responseData.text();
  if (!text) return "";

  try {
    const data = JSON.parse(text) as {
      message?: string;
      error?: string;
      detail?: string;
    };

    return data.message || data.error || data.detail || "";
  } catch {
    return text;
  }
};

export default function ExpressData() {
  const defaultDate = useMemo(() => getDefaultExpressDate(), []);
  const role = useUserRole();
  const canSelectBranch = featureAccess.expressDataAllBranches(role);
  const branches = useBranches(canSelectBranch);

  const [transactionDate, setTransactionDate] = useState<Dayjs>(defaultDate);
  const [selectedRoute, setSelectedRoute] = useState<string>("all");
  const [transactionType, setTransactionType] = useState<
    ExpressDataTransactionType | "all"
  >("all");
  const [status, setStatus] = useState<ExpressDataStatus[]>(["pending", "paid", "approve"]);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [rows, setRows] = useState<ExpressDataRow[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalNetValue, setTotalNetValue] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  // Starts on the user's own branch; head office and master may then pick another.
  const [branchCode, setBranchCode] = useState<string>("");

  useEffect(() => {
    const onGetProfile = async () => {
      const profile = await getProfile();
      setBranchCode(profile.attributes.branchCode.toString());
    };

    onGetProfile();
  }, []);

  const transactionDateParam = useMemo(
    () => getExpressDataTransactionDateParam(transactionDate),
    [transactionDate],
  );

  const summary = useMemo(
    () => getExpressDataSummary(totalCount, totalNetValue),
    [totalCount, totalNetValue],
  );

  const fetchExpressData = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await getExpressData({
        page,
        limit,
        transaction_date: transactionDateParam,
        branch_code: branchCode,
        route_code: selectedRoute,
        transaction_type:
          transactionType !== "all" ? transactionType : undefined,
        status: status.length > 0 ? status : undefined,
      });

      const responseData = data ?? EMPTY_EXPRESS_DATA_RESPONSE;

      setRows(responseData.rows);
      setTotalCount(responseData.count);
      setTotalNetValue(Number(responseData.total_net_value ?? 0));
      setTotalPages(responseData.totalPages);
      if (responseData.page !== page) setPage(responseData.page);
    } catch (error) {
      console.error("Error fetching express data:", error);
    } finally {
      setLoading(false);
    }
  }, [
    branchCode,
    limit,
    page,
    selectedRoute,
    status,
    transactionDateParam,
    transactionType,
  ]);

  useEffect(() => {
    if (branchCode) {
      fetchExpressData();
    }
  }, [branchCode, fetchExpressData]);

  useEffect(
    () => setPage(1),
    [branchCode, transactionDate, selectedRoute, status, transactionType, limit],
  );

  const handleChangeBranch = (value: string) => {
    setBranchCode(value);
    setSelectedRoute("all");
  };

  const handleChangeLimit = (event: SelectChangeEvent<number>) => {
    setLimit(Number(event.target.value));
    setPage(1);
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const response = await exportExpressData({
        transaction_date: transactionDateParam,
        branch_code: branchCode,
        route_code: selectedRoute,
        transaction_type:
          transactionType !== "all" ? transactionType : undefined,
        status: status.length > 0 ? status : undefined,
      });
      const filename =
        getFilenameFromContentDisposition(
          response.headers["content-disposition"],
        ) || getExpressDataCsvFilename();

      downloadCsv(filename, response.data);

      // The CSV is fine except for its QRPAY column: the backend could not read the
      // QR payment statuses out of vansale. Warn AFTER the download so the file is
      // already saved, and never block on it — an incomplete file the user knows
      // about beats a complete-looking one they don't.
      if (response.headers["x-payment-warning"]) {
        pushGlobalError("ExpressQrPaymentUnavailable");
      }
    } catch (error) {
      const message = await getExportErrorMessage(error);
      if (message) pushGlobalError(message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Typography variant="h6" mb={2}>
          ข้อมูล Express
        </Typography>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleExportCsv}
          disabled={exporting}
          sx={{
            width: { xs: "100%", sm: "auto" },
            ml: { sm: "auto" },
            whiteSpace: "nowrap",
          }}
        >
          Export CSV
        </Button>
      </Box>

      <Card sx={{ boxShadow: "none", borderRadius: "12px" }}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Stack gap={2}>
            <Stack
              flexDirection="row"
              alignItems="flex-end"
              sx={{
                width: "100%",
                gap: 2,
                [compact]: { flexWrap: "wrap", gap: 1.5 },
              }}
            >
              <ExpressDataFilter
                canSelectBranch={canSelectBranch}
                branches={branches}
                branchCode={branchCode}
                onChangeBranch={handleChangeBranch}
                transactionDate={transactionDate}
                setTransactionDate={setTransactionDate}
                selectedRoute={selectedRoute}
                setSelectedRoute={setSelectedRoute}
                transactionType={transactionType}
                setTransactionType={setTransactionType}
                status={status}
                setStatus={setStatus}
              />
              <ExpressDataSummaryCards summary={summary} />
            </Stack>
            <ExpressDataTable data={rows} loading={loading} />
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              p={2}
              sx={{
                [compact]: { p: 1, gap: 1.5, flexWrap: "wrap" },
              }}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="body2">แสดง</Typography>
                <Select size="small" value={limit} onChange={handleChangeLimit}>
                  {LIST_LIMIT.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="body2">รายการ</Typography>
              </Box>
              <Pagination
                count={totalPages || Math.ceil(totalCount / limit)}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
                color="primary"
                variant="outlined"
                shape="rounded"
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
